/*
 * TealerLab — modèle des cadeaux de palier, partagé par le tiroir et la page panier.
 *
 * Un cadeau par palier « cadeau », choisi PALIER PAR PALIER : le client choisit celui du
 * palier atteint le plus bas, et le palier suivant apparaît dès que c'est fait (déverrouillé
 * s'il est atteint, en aperçu avec le montant restant sinon).
 *
 * La ligne de panier porte son palier — `_tlr_tier: "t2"` — sans quoi rien n'est traitable :
 * l'ancien modèle comptait les cadeaux et retirait « le plus ancien » quand le panier
 * repassait sous un seuil, donc n'importe lequel. Avec le palier sur la ligne, on retire
 * exactement le cadeau du palier perdu.
 *
 * La remise ne lit que `_tlr` et `_tlr_src` (2 clés, limite de l'input Multi Mix de
 * SupaEasy) : `_tlr_tier` est une information de thème, invisible de la function.
 *
 * L'état n'est JAMAIS gardé en mémoire : il se redérive du panier à chaque rendu. Deux
 * onglets, un retrait depuis la page panier ou un rechargement donnent donc le même écran.
 */
(function () {
  'use strict';
  const TLR_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const GIFT_SRC = 'gift';

  /**
   * Propriétés d'une ligne cadeau ajoutée par le thème.
   *
   * `_tlr_src` porte le palier (`gift:t2`) : la remise ne lit que DEUX clés de propriété
   * (limite de l'input Multi Mix), donc c'est le seul moyen de lui dire à partir de quel
   * montant ce cadeau est dû. Sans ça, elle offrirait n'importe quelle ligne marquée
   * « cadeau », quel que soit le panier. `_tlr_tier` reste pour le thème, qui lui voit tout.
   */
  function props(tierKey) {
    return {
      _tlr: 'v2',
      _tlr_src: GIFT_SRC + ':' + String(tierKey),
      _is_gift: 'true',
      _tlr_tier: String(tierKey),
    };
  }

  function isGiftLine(line) {
    const p = (line && line.properties) || {};
    return p._is_gift === 'true' || p._is_gift === true;
  }

  function giftLines(items) {
    return (items || []).filter(isGiftLine);
  }

  /**
   * Cadeaux que Shopify refuse. `available: false` vient du palier (Liquid), le reste
   * s'apprend au premier 422 : un palier dont AUCUN cadeau n'est ajoutable ne doit pas
   * ouvrir — sinon le client reste bloqué dessus et ne voit jamais les paliers suivants.
   */
  const unavailable = new Set();
  function markUnavailable(variantId) {
    unavailable.add(String(variantId));
  }
  function usableRewards(rewards) {
    return (rewards || []).filter((r) => r && r.available !== false && !unavailable.has(String(r.id)));
  }

  function tierOfLine(line) {
    const p = (line && line.properties) || {};
    return p._tlr_tier ? String(p._tlr_tier) : null;
  }

  /**
   * Photo de l'état des cadeaux pour un panier donné.
   *
   * `stale` = les lignes à retirer : palier disparu du customizer, palier repassé sous son
   * seuil, ou doublon. On garde la ligne la PLUS RÉCENTE d'un palier (Shopify ajoute en fin
   * de panier) : pendant un changement de cadeau, le nouveau choix est ajouté avant que
   * l'ancien parte, et c'est le nouveau qui doit survivre.
   */
  function computeState(tiers, items, total) {
    const giftTiers = (tiers || [])
      .filter((t) => t && t.type === 'gift' && Array.isArray(t.rewards) && t.rewards.length > 0)
      .slice()
      .sort((a, b) => a.cents - b.cents);

    const lines = giftLines(items);

    // Aucun palier cadeau connu (config d'un store resté sur l'ancien modèle, ou catalogue
    // pas encore résolu) : on ne rend rien ET on ne retire rien. Nettoyer ici reviendrait à
    // manger les cadeaux déjà au panier avant même de savoir à quel palier ils appartiennent.
    if (!giftTiers.length) {
      return { rows: [], claimed: [], active: null, next: null, stale: [], any: false, allClaimed: false };
    }

    const byTier = new Map();
    const stale = [];
    const orphans = [];

    for (const line of lines) {
      const key = tierOfLine(line);
      if (!key) { orphans.push(line); continue; }
      const tier = giftTiers.find((t) => t.key === key);
      if (!tier || total < tier.cents) { stale.push({ line, tier: tier || null }); continue; }
      const previous = byTier.get(key);
      if (previous) stale.push({ line: previous, tier });
      byTier.set(key, line);
    }

    // Une ligne cadeau d'avant ce modèle (sans `_tlr_tier`) est ADOPTÉE par le palier libre
    // le plus bas plutôt que retirée : le client ne doit pas perdre son cadeau parce qu'on
    // a changé de mécanique entre deux visites.
    for (const line of orphans) {
      const tier = giftTiers.find((t) => total >= t.cents && !byTier.has(t.key));
      if (tier) byTier.set(tier.key, line);
      else stale.push({ line, tier: null });
    }

    const rows = giftTiers.map((tier) => {
      const rewards = usableRewards(tier.rewards);
      return {
        tier,
        reached: total >= tier.cents,
        line: byTier.get(tier.key) || null,
        rewards,
        // Palier atteint dont plus rien n'est ajoutable : on le passe au lieu d'y enfermer
        // le client (c'est le cas de t2 et t3 sur lab, dont les 10 g sont épuisés).
        blocked: total >= tier.cents && rewards.length === 0 && !byTier.get(tier.key),
      };
    });

    const claimed = rows.filter((r) => r.line);
    const active = rows.find((r) => r.reached && !r.line && r.rewards.length > 0) || null;
    // On n'annonce que ce qu'on peut réellement offrir : un palier dont tous les cadeaux
    // sont épuisés ne sert pas d'appât.
    const next = rows.find((r) => !r.reached && r.rewards.length > 0) || null;

    return {
      rows,
      claimed,
      active,
      next,
      stale,
      any: giftTiers.length > 0,
      allClaimed: !active && claimed.length > 0,
    };
  }

  /**
   * Paliers à utiliser, repli compris.
   *
   * Un store resté sur l'ancien modèle n'a aucun palier typé « cadeau » : ses cadeaux sont
   * nommés dans les réglages généraux, au seuil historique. On en fabrique alors UN palier,
   * une seule fois, pour que la colonne continue d'exister — et surtout pour que le tiroir
   * et la page panier raisonnent sur la même liste.
   */
  let legacyTier = null;
  let legacyPending = null;

  function tiersOf(tiers) {
    return legacyTier ? (tiers || []).concat([legacyTier]) : tiers || [];
  }

  function ensureTiers(tiers, legacy) {
    if (legacyTier || !legacy) return Promise.resolve(tiersOf(tiers));
    if ((tiers || []).some((t) => t.type === 'gift' && (t.rewards || []).length)) {
      return Promise.resolve(tiers || []);
    }
    if (!legacy.cents || !(legacy.handles || []).length) return Promise.resolve(tiers || []);
    if (!legacyPending) {
      legacyPending = Promise.all(
        legacy.handles.slice(0, 2).map((h) =>
          fetch(TLR_ROOT + 'products/' + encodeURIComponent(h) + '.js')
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      ).then((products) => {
        const rewards = products
          .filter(Boolean)
          .map((p, idx) => {
            const wanted = String((legacy.variantTitles || [])[idx] || '').toLowerCase();
            const all = p.variants || [];
            // On ne filtre PAS sur `available` : un cadeau est souvent marqué indisponible
            // côté storefront alors qu'il reste ajoutable au panier.
            const chosen = (wanted && all.find((v) => String(v.title || '').toLowerCase() === wanted)) || all[0];
            if (!chosen) return null;
            const vTitle = String(chosen.title || '').toLowerCase() === 'default title' ? '' : chosen.title || '';
            return { id: chosen.id, title: p.title, handle: p.handle, image: p.featured_image, variantTitle: vTitle };
          })
          .filter(Boolean);
        if (rewards.length) {
          legacyTier = { key: 'gift', cfg: 'gift', cents: legacy.cents, label: '', type: 'gift', rewards };
        }
      });
    }
    return legacyPending.then(() => tiersOf(tiers));
  }

  /* ── Rendu ────────────────────────────────────────────────────────────────
     Les fonctions reçoivent le contexte du consommateur (échappement, format
     monétaire, chaînes traduites, nom court) : ni le tiroir ni la page panier ne
     dupliquent ces règles, et aucune chaîne n'est écrite en dur ici. */

  function fill(template, token, value) {
    return String(template || '').split('{' + token + '}').join(value);
  }

  /** Boutons de choix d'un palier. `selectedId` marque le cadeau déjà pris (mode changement). */
  function gridHtml(row, ctx, selectedId) {
    const esc = ctx.escapeHtml;
    return (row.rewards || [])
      .map((r) => {
        const variantTitle = String(r.variantTitle || '');
        const selected = selectedId && String(r.id) === String(selectedId);
        return (
          '<button type="button" class="tlr-cart__sample-btn' +
          (variantTitle ? ' tlr-cart__sample-btn--variant' : '') +
          (selected ? ' is-selected' : '') +
          '" data-gift-variant="' + esc(String(r.id)) + '"' +
          ' data-gift-tier="' + esc(String(row.tier.key)) + '"' +
          ' data-gift-handle="' + esc(r.handle || '') + '">' +
          (r.image ? '<img src="' + esc(r.image) + '" alt="' + esc(r.title || '') + '" loading="lazy">' : '') +
          '<span class="tlr-cart__sample-name">' + esc(ctx.shortName(r.title)) + '</span>' +
          (variantTitle ? '<span class="tlr-cart__sample-variant">' + esc(variantTitle) + '</span>' : '') +
          '</button>'
        );
      })
      .join('');
  }

  /** Récap des paliers déjà choisis : vignette, nom, et de quoi changer d'avis. */
  function claimedHtml(state, ctx) {
    const esc = ctx.escapeHtml;
    if (!state.claimed.length) return '';
    const items = state.claimed
      .map((row) => {
        const line = row.line;
        const img = line.image ? '<img src="' + esc(line.image) + '" alt="" loading="lazy">' : '';
        const name = esc(ctx.shortName(line.product_title || line.title));
        return (
          '<li class="tlr-cart__gift-chip" data-gift-claimed-tier="' + esc(String(row.tier.key)) + '">' +
          img +
          '<span class="tlr-cart__gift-chip-name">' + name + '</span>' +
          '<span class="tlr-cart__gift-chip-amount">' + esc(ctx.fmtMoney(row.tier.cents)) + '</span>' +
          '<button type="button" class="tlr-cart__gift-chip-edit" data-gift-edit="' + esc(String(row.tier.key)) + '">' +
          esc(ctx.i18n.giftsChange || 'Changer') +
          '</button>' +
          '</li>'
        );
      })
      .join('');
    // Pas de titre ici : l'entête de la colonne le porte déjà (headline).
    return '<ul class="tlr-cart__gift-claimed-list">' + items + '</ul>';
  }

  /** Aperçu du prochain palier : ce qu'il offre, et ce qu'il reste à mettre au panier. */
  function lockedHtml(row, total, ctx) {
    const esc = ctx.escapeHtml;
    const delta = Math.max(0, row.tier.cents - total);
    const thumbs = (row.rewards || [])
      .map((r) => (r.image ? '<img src="' + esc(r.image) + '" alt="" loading="lazy">' : ''))
      .join('');
    const msg = fill(ctx.i18n.giftsTierLocked || '', 'delta', '<strong>' + esc(ctx.fmtMoney(delta)) + '</strong>');
    return (
      '<div class="tlr-cart__gift-teaser">' +
      (thumbs ? '<div class="tlr-cart__gift-teaser-thumbs">' + thumbs + '</div>' : '') +
      '<span class="tlr-cart__gift-locked-msg">' + msg + '</span>' +
      '</div>'
    );
  }

  /** Titre de la colonne selon l'état — le palier actif annonce son montant. */
  function headline(state, ctx) {
    if (state.active) return fill(ctx.i18n.giftsTierPick || '', 'amount', ctx.fmtMoney(state.active.tier.cents));
    // « Tous tes cadeaux sont choisis » ne se dit que s'il n'y a plus rien à débloquer :
    // avec un palier suivant en vue, l'entête annonce le récap, l'aperçu dit le reste.
    if (state.claimed.length && !state.next) return ctx.i18n.giftsAllClaimed || '';
    if (state.claimed.length) return ctx.i18n.giftsClaimedTitle || ctx.i18n.giftsPickOne || '';
    return ctx.i18n.giftsPickOne || '';
  }

  /* ── Mutations ────────────────────────────────────────────────────────────
     Toutes sérialisées : deux clics rapides ne doivent pas se croiser et laisser
     deux cadeaux sur un palier, ou retirer celui qui vient d'être ajouté. */

  let chain = Promise.resolve();
  function run(task) {
    const next = chain.then(task, task);
    chain = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  }

  async function addOnce(variantId, tierKey) {
    const res = await fetch(TLR_ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props(tierKey) }] }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.description || body.message || 'add failed');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  /**
   * Un 422 ne veut pas toujours dire « épuisé » : le panier répond aussi ça quand une autre
   * écriture est en vol (constaté au test du 2026-09-21 — le même cadeau refusé, puis accepté
   * à la seconde d'après). On retente UNE fois avant de conclure, sinon un cadeau parfaitement
   * disponible se retrouve barré pour toute la visite.
   */
  async function add(variantId, tierKey, attempts = 2) {
    let lastError;
    for (let i = 0; i < attempts; i++) {
      try {
        return await addOnce(variantId, tierKey);
      } catch (err) {
        lastError = err;
        if (err.status !== 422 || i === attempts - 1) break;
        await new Promise((r) => setTimeout(r, 400));
      }
    }
    throw lastError;
  }

  async function removeKey(key) {
    const res = await fetch(TLR_ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!res.ok) throw new Error('remove failed');
    return res.json();
  }

  /**
   * Changer le cadeau d'un palier : on AJOUTE d'abord, on retire ensuite. Dans l'autre
   * sens, un ajout qui échoue (variante épuisée — c'est le cas de deux cadeaux configurés
   * aujourd'hui) laisserait le client sans rien.
   */
  async function swap(oldKey, variantId, tierKey) {
    const cart = await add(variantId, tierKey);
    try {
      return await removeKey(oldKey);
    } catch (_) {
      return cart;
    }
  }

  window.TlrGiftTiers = {
    props,
    tiersOf,
    ensureTiers,
    markUnavailable,
    isGiftLine,
    giftLines,
    tierOfLine,
    computeState,
    gridHtml,
    claimedHtml,
    lockedHtml,
    headline,
    fill,
    run,
    add,
    removeKey,
    swap,
  };
})();
